import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Rnd } from "react-rnd";
import { toPng } from "html-to-image";
import {
  TextAa, Trash, Download, Plus, Image as ImageIcon, Copy,
  ArrowsClockwise, Rectangle, Square, VideoCamera, InstagramLogo,
  StackSimple, ArrowDown, FloppyDisk, FolderOpen, X, Sticker, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { useSeo } from "@/lib/seo";
import { STICKERS, STICKER_CATEGORIES, getSticker } from "@/lib/stickers";
import { SHAPES, SHAPE_CATEGORIES, getShape, renderShapeSvg, shapePreviewSvg } from "@/lib/shapes";
import { magicCut, featherSubject } from "@/lib/magic";

/* ---------------- canvas format presets ---------------- */
const FORMATS = [
  { key: "reel", label: "Reel / Short", w: 1080, h: 1920, icon: VideoCamera, badge: "9:16" },
  { key: "story", label: "Story", w: 1080, h: 1920, icon: InstagramLogo, badge: "9:16" },
  { key: "post", label: "Instagram Post", w: 1080, h: 1080, icon: Square, badge: "1:1" },
  { key: "thumbnail", label: "YouTube Thumbnail", w: 1280, h: 720, icon: Rectangle, badge: "16:9" },
];

/* ---------------- fonts + presets (from previous ThumbnailStudio) ---------------- */
const FONTS = [
  { family: "Anton", label: "Anton — tall & bold" },
  { family: "Bebas Neue", label: "Bebas Neue — condensed" },
  { family: "Archivo Black", label: "Archivo Black — chunky" },
  { family: "Oswald", label: "Oswald — narrow" },
  { family: "Bungee", label: "Bungee — arcade" },
  { family: "Passion One", label: "Passion One — impact" },
  { family: "Righteous", label: "Righteous — display" },
  { family: "Montserrat", label: "Montserrat Black" },
  { family: "Inter", label: "Inter — clean" },
];

const PRESETS = [
  { key: "beast", name: "Beast", style: { color: "#FFFFFF", strokeColor: "#000000", strokeWidth: 14, shadow: 12, gradient: null, tape: false }, font: "Anton" },
  { key: "red-stroke", name: "Bold Red", style: { color: "#FFFFFF", strokeColor: "#E11D2E", strokeWidth: 12, shadow: 8, gradient: null, tape: false }, font: "Archivo Black" },
  { key: "yellow-gradient", name: "Yellow Gradient", style: { color: null, strokeColor: "#000000", strokeWidth: 10, shadow: 8, gradient: ["#FFE55C", "#FF9900"], tape: false }, font: "Passion One" },
  { key: "3d-shadow", name: "3D Shadow", style: { color: "#FFFFFF", strokeColor: "#000000", strokeWidth: 6, shadow: 18, gradient: null, tape: false }, font: "Bungee" },
  { key: "cutout-tape", name: "Cutout Tape", style: { color: "#0B0B0F", strokeColor: null, strokeWidth: 0, shadow: 0, gradient: null, tape: true }, font: "Bebas Neue" },
  { key: "glow-purple", name: "Neon Glow", style: { color: "#FFFFFF", strokeColor: null, strokeWidth: 0, shadow: 24, gradient: null, tape: false, shadowColor: "#7C5CFF" }, font: "Righteous" },
  { key: "clean-white", name: "Clean White", style: { color: "#FFFFFF", strokeColor: null, strokeWidth: 0, shadow: 8, gradient: null, tape: false }, font: "Inter" },
];

/* ---------------- fonts loader (once) ---------------- */
const loadFontsOnce = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Anton&family=Archivo+Black&family=Bebas+Neue&family=Bungee&family=Montserrat:wght@900&family=Oswald:wght@700&family=Passion+One:wght@900&family=Righteous&display=swap";
    document.head.appendChild(link);
  };
})();

/* ---------------- helpers ---------------- */
const newTextEl = (fmt) => ({
  id: crypto.randomUUID(),
  type: "text",
  text: "YOUR TEXT",
  fontFamily: "Anton",
  fontSize: Math.round(fmt.w * 0.10),
  color: "#FFFFFF",
  strokeColor: "#000000",
  strokeWidth: Math.max(6, Math.round(fmt.w * 0.008)),
  shadow: 10,
  gradient: null,
  tape: false,
  textAlign: "center",
  x: Math.round(fmt.w * 0.22),
  y: Math.round(fmt.h * 0.42),
  width: Math.round(fmt.w * 0.55),
  height: Math.round(fmt.w * 0.18),
  rotation: 0,
});

const newImageEl = (fmt, src, w, h) => {
  // fit image inside 60% of stage width
  const targetW = Math.round(fmt.w * 0.6);
  const scale = targetW / w;
  return {
    id: crypto.randomUUID(),
    type: "image",
    src,
    x: Math.round((fmt.w - targetW) / 2),
    y: Math.round((fmt.h - h * scale) / 2),
    width: targetW,
    height: Math.round(h * scale),
    rotation: 0,
  };
};

const newStickerEl = (fmt, stickerKey) => {
  const size = Math.round(fmt.w * 0.22);
  return {
    id: crypto.randomUUID(),
    type: "svg",
    stickerKey,
    color: "#FFFFFF",
    x: Math.round((fmt.w - size) / 2),
    y: Math.round((fmt.h - size) / 2),
    width: size,
    height: size,
    rotation: 0,
  };
};

const newShapeEl = (fmt, shapeKey) => {
  const def = getShape(shapeKey);
  const size = Math.round(fmt.w * 0.35);
  return {
    id: crypto.randomUUID(),
    type: "shape",
    shapeKey,
    fill: def.fill,
    stroke: def.stroke,
    strokeW: def.sw,
    x: Math.round((fmt.w - size) / 2),
    y: Math.round((fmt.h - size) / 2),
    width: size,
    height: size,
    rotation: 0,
  };
};

const newMagicEl = (fmt, src, subjectSrc, w, h) => {
  const targetW = Math.round(fmt.w * 0.85);
  const scale = targetW / w;
  return {
    id: crypto.randomUUID(),
    type: "magic",
    src,
    subjectSrc,
    x: Math.round((fmt.w - targetW) / 2),
    y: Math.round((fmt.h - h * scale) / 2),
    width: targetW,
    height: Math.round(h * scale),
    rotation: 0,
  };
};

const textStyle = (el) => {
  const base = {
    fontFamily: `"${el.fontFamily}", Impact, sans-serif`,
    fontSize: `${el.fontSize}px`,
    lineHeight: 1.05,
    textAlign: el.textAlign || "center",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: el.textAlign === "left" ? "flex-start" : el.textAlign === "right" ? "flex-end" : "center",
    userSelect: "none",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    padding: el.tape ? `${el.fontSize * 0.1}px ${el.fontSize * 0.3}px` : 0,
  };
  if (el.tape) {
    base.background = "#FFE55C";
    base.color = el.color || "#0B0B0F";
    base.display = "inline-flex";
  } else if (el.gradient) {
    base.background = `linear-gradient(180deg, ${el.gradient[0]}, ${el.gradient[1]})`;
    base.WebkitBackgroundClip = "text";
    base.WebkitTextFillColor = "transparent";
    base.backgroundClip = "text";
    // Stroke via text-shadow trick (stroke on gradient text)
    if (el.strokeColor && el.strokeWidth > 0) {
      const s = el.strokeWidth;
      base.textShadow = [
        `${s}px 0 0 ${el.strokeColor}`, `-${s}px 0 0 ${el.strokeColor}`,
        `0 ${s}px 0 ${el.strokeColor}`, `0 -${s}px 0 ${el.strokeColor}`,
        `${s}px ${s}px 0 ${el.strokeColor}`, `-${s}px -${s}px 0 ${el.strokeColor}`,
        `${s}px -${s}px 0 ${el.strokeColor}`, `-${s}px ${s}px 0 ${el.strokeColor}`,
      ].join(",");
    }
  } else {
    base.color = el.color || "#FFFFFF";
    if (el.strokeColor && el.strokeWidth > 0) {
      base.WebkitTextStroke = `${el.strokeWidth}px ${el.strokeColor}`;
      base.paintOrder = "stroke fill";
    }
    if (el.shadow) {
      base.filter = `drop-shadow(0 ${Math.max(2, el.shadow / 3)}px ${el.shadow}px ${el.shadowColor || "rgba(0,0,0,0.6)"})`;
    }
  }
  return base;
};

/* ---------------- rotation handle logic ---------------- */
const useRotateHandle = (el, updateEl, scale) => {
  const start = useRef(null);
  const onPointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const initialAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    start.current = { cx, cy, initialAngle, initialRot: el.rotation };
    const onMove = (ev) => {
      const ang = Math.atan2(ev.clientY - start.current.cy, ev.clientX - start.current.cx) * (180 / Math.PI);
      const delta = ang - start.current.initialAngle;
      updateEl({ rotation: Math.round(start.current.initialRot + delta) });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      start.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  return onPointerDown;
  // scale kept for possible future use
  // eslint-disable-next-line
};

/* ---------------- ElementView (Rnd + rotate wrapper) ---------------- */
const ElementView = ({ el, selected, onSelect, onUpdate, onDragging, onDragEnd, onResizing, scale, format }) => {
  const startRotate = useRotateHandle(el, onUpdate, scale);
  let inner;
  if (el.type === "text") {
    inner = <div style={textStyle(el)} data-testid={`el-text-${el.id}`}>{el.text}</div>;
  } else if (el.type === "image") {
    inner = (
      <img
        src={el.src}
        alt=""
        draggable={false}
        style={{ width: "100%", height: "100%", objectFit: "contain", userSelect: "none", pointerEvents: "none" }}
        data-testid={`el-image-${el.id}`}
      />
    );
  } else if (el.type === "magic") {
    inner = (
      <img
        src={el.src}
        alt=""
        draggable={false}
        style={{ width: "100%", height: "100%", objectFit: "fill", userSelect: "none", pointerEvents: "none" }}
        data-testid={`el-magic-${el.id}`}
      />
    );
  } else if (el.type === "shape") {
    const s = getShape(el.shapeKey);
    inner = (
      <div
        data-testid={`el-shape-${el.id}`}
        style={{ width: "100%", height: "100%" }}
        dangerouslySetInnerHTML={{ __html: s ? renderShapeSvg(s, el) : "" }}
      />
    );
  } else if (el.type === "svg") {
    const s = getSticker(el.stickerKey);
    inner = (
      <div
        data-testid={`el-svg-${el.id}`}
        style={{ width: "100%", height: "100%", color: el.color || "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center" }}
        dangerouslySetInnerHTML={{ __html: s ? s.svg.replace("<svg ", '<svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" ') : "" }}
      />
    );
  }
  return (
    <Rnd
      position={{ x: el.x, y: el.y }}
      size={{ width: el.width, height: el.height }}
      onDragStart={() => onSelect(el.id)}
      onDrag={(_, d) => onDragging && onDragging(el, { x: d.x, y: d.y })}
      onDragStop={(_, d) => {
        const snapped = onDragEnd ? onDragEnd(el, { x: d.x, y: d.y }) : { x: d.x, y: d.y };
        onUpdate({ x: Math.round(snapped.x), y: Math.round(snapped.y) });
      }}
      onResizeStart={() => onSelect(el.id)}
      onResize={(_, __, ref, ___, pos) => onResizing && onResizing(el, {
        x: pos.x,
        y: pos.y,
        width: parseFloat(ref.style.width),
        height: parseFloat(ref.style.height),
      })}
      onResizeStop={(_, __, ref, ___, pos) => {
        onUpdate({
          width: Math.round(parseFloat(ref.style.width)),
          height: Math.round(parseFloat(ref.style.height)),
          x: Math.round(pos.x),
          y: Math.round(pos.y),
        });
      }}
      scale={scale}
      enableResizing={{ top: true, right: true, bottom: true, left: true, topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }}
      style={{ zIndex: selected ? 20 : el.overSubject ? 11 : 1, touchAction: "none" }}
      className={selected ? "el-selected" : "el-idle"}
    >
      <div
        onMouseDown={() => onSelect(el.id)}
        onTouchStart={() => onSelect(el.id)}
        data-el-inner="true"
        style={{
          width: "100%",
          height: "100%",
          transform: `rotate(${el.rotation}deg)`,
          transformOrigin: "center center",
          position: "relative",
        }}
      >
        {inner}
        {selected && (
          <div
            onPointerDown={startRotate}
            data-testid={`rotate-handle-${el.id}`}
            title="Drag to rotate"
            style={{
              position: "absolute",
              top: -34 / scale,
              left: "50%",
              transform: "translateX(-50%)",
              width: 26 / scale,
              height: 26 / scale,
              borderRadius: "50%",
              background: "#7C5CFF",
              border: `${2 / scale}px solid #fff`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 30,
              touchAction: "none",
            }}
          >
            <ArrowsClockwise size={14 / scale} color="#fff" weight="bold" />
          </div>
        )}
      </div>
    </Rnd>
  );
};

/* ---------------- feathered subject overlay ---------------- */
const featherCache = new Map();
const SubjectOverlay = ({ el }) => {
  const [src, setSrc] = useState(el.subjectSrc);
  useEffect(() => {
    let alive = true;
    const f = el.feather || 0;
    if (!f) { setSrc(el.subjectSrc); return; }
    const key = `${el.id}:${f}`;
    const cached = featherCache.get(key);
    if (cached) { setSrc(cached); return; }
    const t = setTimeout(() => {
      featherSubject(el.subjectSrc, f)
        .then((d) => {
          if (featherCache.size > 40) featherCache.clear();
          featherCache.set(key, d);
          if (alive) setSrc(d);
        })
        .catch(() => {});
    }, 120);
    return () => { alive = false; clearTimeout(t); };
  }, [el.subjectSrc, el.feather, el.id]);
  return <img src={src} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "fill" }} />;
};

/* ---------------- Main ---------------- */
export default function Studio() {
  useSeo(
    "VidhFlow Studio — Free Reel, Story & Thumbnail Editor",
    "Design vertical Reels, Stories, Instagram posts and YouTube thumbnails in your browser. Drag, resize and rotate text and images. Export PNG."
  );

  const [formatKey, setFormatKey] = useState("reel");
  const format = FORMATS.find((f) => f.key === formatKey);
  const [bgColor, setBgColor] = useState("#0B0B0F");
  const [bgImage, setBgImage] = useState(null); // data URL
  const [elements, setElements] = useState(() => [newTextEl(FORMATS[0])]);
  const [selectedId, setSelectedId] = useState(elements[0]?.id || null);
  const [scale, setScale] = useState(0.3);
  const [exporting, setExporting] = useState(false);
  const [snapLines, setSnapLines] = useState([]); // [{o:'v'|'h', pos:number}]
  const [liveEl, setLiveEl] = useState(null); // live x/y/w/h while dragging/resizing (keeps subject overlay in sync)
  const [savedDesigns, setSavedDesigns] = useState(() => {
    try { return JSON.parse(localStorage.getItem("studio_designs") || "[]"); } catch { return []; }
  });
  const [showDesigns, setShowDesigns] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [stickerCat, setStickerCat] = useState("arrows");
  const [shapeCat, setShapeCat] = useState("blobs");
  const [magicBusy, setMagicBusy] = useState(false);
  const restoredRef = useRef(false);

  const stageRef = useRef(null);
  const stageWrapRef = useRef(null);
  const imgInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const magicInputRef = useRef(null);

  const selected = elements.find((e) => e.id === selectedId);

  /* fonts */
  useEffect(() => { loadFontsOnce(); }, []);

  /* Restore auto-saved WIP on mount */
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = localStorage.getItem("studio_current");
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s.formatKey) setFormatKey(s.formatKey);
      if (s.bgColor) setBgColor(s.bgColor);
      if (s.bgImage !== undefined) setBgImage(s.bgImage);
      if (Array.isArray(s.elements) && s.elements.length) {
        setElements(s.elements);
        setSelectedId(s.elements[0]?.id || null);
      }
    } catch {}
  }, []);

  /* Auto-save WIP (debounced) */
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          "studio_current",
          JSON.stringify({ formatKey, bgColor, bgImage, elements })
        );
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [formatKey, bgColor, bgImage, elements]);

  /* Clamp element positions into canvas whenever format changes */
  useEffect(() => {
    setElements((es) => es.map((el) => ({
      ...el,
      x: Math.min(Math.max(0, el.x), Math.max(0, format.w - el.width)),
      y: Math.min(Math.max(0, el.y), Math.max(0, format.h - el.height)),
      width: Math.min(el.width, format.w),
      height: Math.min(el.height, format.h),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatKey]);

  /* compute scale to fit viewport */
  useEffect(() => {
    const compute = () => {
      const wrap = stageWrapRef.current;
      if (!wrap) return;
      const availW = wrap.clientWidth;
      const availH = Math.min(window.innerHeight - 240, 720);
      const s = Math.min(availW / format.w, availH / format.h);
      setScale(Math.max(0.08, s));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [format.w, format.h]);

  /* ---------------- ops ---------------- */
  const addText = () => {
    const el = newTextEl(format);
    setElements((es) => [...es, el]);
    setSelectedId(el.id);
  };

  const addImage = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      const img = new Image();
      img.onload = () => {
        const el = newImageEl(format, src, img.naturalWidth, img.naturalHeight);
        setElements((es) => [...es, el]);
        setSelectedId(el.id);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const addSticker = (stickerKey) => {
    const el = newStickerEl(format, stickerKey);
    setElements((es) => [...es, el]);
    setSelectedId(el.id);
    setShowStickers(false);
  };

  const addShape = (shapeKey) => {
    const el = newShapeEl(format, shapeKey);
    setElements((es) => [...es, el]);
    setSelectedId(el.id);
  };

  const addMagic = async (file) => {
    setMagicBusy(true);
    toast.loading("Magic Layers — starting AI engine…", { id: "magic" });
    try {
      const { src, subjectSrc, w, h } = await magicCut(file, (stage, backend) => {
        toast.loading(
          stage === "model"
            ? "Magic Layers — loading AI model…"
            : stage === "retry"
            ? `Magic Layers — engine stalled, retrying with a more compatible one (${backend})…`
            : backend === "cpu"
            ? "Magic Layers — cutting out (compatible mode, a few extra seconds)…"
            : "Magic Layers — cutting out your subject…",
          { id: "magic" }
        );
      });
      const el = newMagicEl(format, src, subjectSrc, w, h);
      setElements((es) => [el, ...es]);
      setSelectedId(el.id);
      toast.success("Subject cut out! New text and shapes now sit behind your subject.", { id: "magic" });
    } catch (err) {
      toast.error(err.message || "Background removal failed — try another photo", { id: "magic" });
    } finally {
      setMagicBusy(false);
    }
  };

  const detachSubject = async () => {
    if (!selected || selected.type !== "magic") return;
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = selected.subjectSrc; });
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      const cctx = c.getContext("2d");
      cctx.drawImage(img, 0, 0);
      const d = cctx.getImageData(0, 0, c.width, c.height).data;
      let minX = c.width, minY = c.height, maxX = 0, maxY = 0;
      for (let y = 0; y < c.height; y++) {
        for (let x = 0; x < c.width; x++) {
          if (d[(y * c.width + x) * 4 + 3] > 10) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX <= minX || maxY <= minY) { toast.error("No subject pixels found"); return; }
      const cw = maxX - minX + 1, ch = maxY - minY + 1;
      const crop = document.createElement("canvas");
      crop.width = cw;
      crop.height = ch;
      crop.getContext("2d").drawImage(c, minX, minY, cw, ch, 0, 0, cw, ch);
      const scaleX = selected.width / c.width;
      const scaleY = selected.height / c.height;
      const el = {
        id: crypto.randomUUID(),
        type: "image",
        isCutout: true,
        src: crop.toDataURL("image/png"),
        x: Math.round(selected.x + minX * scaleX),
        y: Math.round(selected.y + minY * scaleY),
        width: Math.round(cw * scaleX),
        height: Math.round(ch * scaleY),
        rotation: selected.rotation || 0,
      };
      setElements((es) => [...es, el]);
      setSelectedId(el.id);
      toast.success("Subject detached as its own layer — move and resize it freely");
    } catch (e) {
      toast.error("Could not detach subject");
    }
  };

  const uploadBg = (file) => {
    const reader = new FileReader();
    reader.onload = () => setBgImage(reader.result);
    reader.readAsDataURL(file);
  };

  const updateSelected = useCallback((patch) => {
    if (!selectedId) return;
    setElements((es) => es.map((e) => (e.id === selectedId ? { ...e, ...patch } : e)));
  }, [selectedId]);

  const updateEl = useCallback((id, patch) => {
    setElements((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    setLiveEl((l) => (l && l.id === id ? null : l));
  }, []);

  const applyPreset = (preset) => {
    if (!selected || selected.type !== "text") return;
    updateSelected({ ...preset.style, fontFamily: preset.font });
  };

  const deleteSelected = () => {
    if (!selected) return;
    setElements((es) => es.filter((e) => e.id !== selected.id));
    setSelectedId(null);
  };

  const duplicateSelected = () => {
    if (!selected) return;
    const dup = { ...selected, id: crypto.randomUUID(), x: selected.x + 40, y: selected.y + 40 };
    setElements((es) => [...es, dup]);
    setSelectedId(dup.id);
  };

  const bringToFront = () => {
    if (!selected) return;
    setElements((es) => [...es.filter((e) => e.id !== selected.id), selected]);
  };

  const sendToBack = () => {
    if (!selected) return;
    setElements((es) => [selected, ...es.filter((e) => e.id !== selected.id)]);
  };

  /* ---------------- snap guides ---------------- */
  const SNAP_TOL_PX = 8; // pixels on screen
  const snapPos = useCallback((el, x, y) => {
    const tol = SNAP_TOL_PX / scale; // convert to canvas coords
    let sx = x, sy = y;
    const cx = x + el.width / 2;
    const cy = y + el.height / 2;
    // X snapping: canvas centre, left edge, right edge
    if (Math.abs(cx - format.w / 2) < tol) sx = format.w / 2 - el.width / 2;
    else if (Math.abs(x) < tol) sx = 0;
    else if (Math.abs(x + el.width - format.w) < tol) sx = format.w - el.width;
    // Y snapping
    if (Math.abs(cy - format.h / 2) < tol) sy = format.h / 2 - el.height / 2;
    else if (Math.abs(y) < tol) sy = 0;
    else if (Math.abs(y + el.height - format.h) < tol) sy = format.h - el.height;
    return { x: sx, y: sy };
  }, [scale, format.w, format.h]);

  const computeGuides = useCallback((el, x, y) => {
    const lines = [];
    const cx = x + el.width / 2;
    const cy = y + el.height / 2;
    const eps = 0.5;
    if (Math.abs(cx - format.w / 2) < eps) lines.push({ o: "v", pos: format.w / 2, kind: "center" });
    if (Math.abs(x) < eps) lines.push({ o: "v", pos: 0, kind: "edge" });
    if (Math.abs(x + el.width - format.w) < eps) lines.push({ o: "v", pos: format.w, kind: "edge" });
    if (Math.abs(cy - format.h / 2) < eps) lines.push({ o: "h", pos: format.h / 2, kind: "center" });
    if (Math.abs(y) < eps) lines.push({ o: "h", pos: 0, kind: "edge" });
    if (Math.abs(y + el.height - format.h) < eps) lines.push({ o: "h", pos: format.h, kind: "edge" });
    return lines;
  }, [format.w, format.h]);

  const handleDragging = useCallback((el, { x, y }) => {
    const snapped = snapPos(el, x, y);
    setSnapLines(computeGuides(el, snapped.x, snapped.y));
    if (el.type === "magic") setLiveEl({ id: el.id, x, y });
  }, [snapPos, computeGuides]);

  const handleResizing = useCallback((el, patch) => {
    if (el.type === "magic") setLiveEl({ id: el.id, ...patch });
  }, []);

  const handleDragEnd = useCallback((el, { x, y }) => {
    setSnapLines([]);
    return snapPos(el, x, y);
  }, [snapPos]);

  /* ---------------- saved designs ---------------- */
  const persistDesigns = (list) => {
    try { localStorage.setItem("studio_designs", JSON.stringify(list)); } catch (e) {
      toast.error("Save failed — browser storage full");
    }
  };

  const saveDesign = () => {
    const name = window.prompt("Name this design:", `Design ${savedDesigns.length + 1}`);
    if (!name) return;
    const d = {
      id: crypto.randomUUID(),
      name: name.slice(0, 60),
      formatKey, bgColor, bgImage, elements,
      updatedAt: new Date().toISOString(),
    };
    const next = [d, ...savedDesigns];
    setSavedDesigns(next);
    persistDesigns(next);
    toast.success(`Saved "${d.name}"`);
  };

  const loadDesign = (d) => {
    setFormatKey(d.formatKey);
    setBgColor(d.bgColor);
    setBgImage(d.bgImage);
    setElements(d.elements);
    setSelectedId(d.elements[0]?.id || null);
    setShowDesigns(false);
    toast.success(`Loaded "${d.name}"`);
  };

  const deleteDesign = (id) => {
    const next = savedDesigns.filter((d) => d.id !== id);
    setSavedDesigns(next);
    persistDesigns(next);
  };

  const newBlank = () => {
    const el = newTextEl(format);
    setElements([el]);
    setSelectedId(el.id);
    setBgImage(null);
    setBgColor("#0B0B0F");
  };

  /* ---------------- export ---------------- */
  const exportPng = async () => {
    if (!stageRef.current) return;
    setExporting(true);
    setSelectedId(null); // hide selection UI
    await new Promise((r) => setTimeout(r, 60));
    try {
      const node = stageRef.current;
      // temporarily reset scale + clip overflow so bleed-off elements clip cleanly
      const wrap = stageWrapRef.current;
      const prevTransform = node.style.transform;
      const prevOverflow = node.style.overflow;
      node.style.transform = "none";
      node.style.overflow = "hidden";
      wrap.style.height = format.h + "px";
      wrap.style.width = format.w + "px";
      await document.fonts.ready;
      const dataUrl = await toPng(node, { pixelRatio: 1, cacheBust: true, width: format.w, height: format.h, skipFonts: true });
      // restore
      node.style.transform = prevTransform;
      node.style.overflow = prevOverflow;
      wrap.style.height = "";
      wrap.style.width = "";

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `vidhflow-${format.key}-${Date.now()}.png`;
      a.click();
      toast.success(`Downloaded ${format.w}×${format.h} PNG`);
      api.post("/track", { tool: "thumbnail_studio" }).catch(() => {});
    } catch (err) {
      console.error(err);
      toast.error("Export failed — try again");
    } finally {
      setExporting(false);
    }
  };

  /* ---------------- render ---------------- */
  const canvasBg = useMemo(() => {
    if (bgImage) return { backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" };
    return { background: bgColor };
  }, [bgImage, bgColor]);

  return (
    <ToolShell
      slug="thumbnail-studio"
      title="Studio"
      tagline="Design Reels, Stories, posts and YouTube thumbnails. Drag, resize and rotate every element. Export PNG."
      hasResults={true}
    >
      {/* Format selector */}
      <div className="surface rounded-[14px] p-3 mb-4">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {FORMATS.map((f) => (
            <button
              key={f.key}
              data-testid={`format-${f.key}`}
              onClick={() => setFormatKey(f.key)}
              className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-[10px] text-sm font-semibold transition-colors ${
                formatKey === f.key ? "bg-[var(--glow)] text-white" : "border border-[var(--border-c)] text-[var(--muted)] hover:text-[var(--text)]"
              }`}
            >
              <f.icon size={16} weight="duotone" />
              <span>{f.label}</span>
              <span className={`text-[10px] font-mono ${formatKey === f.key ? "text-white/70" : "text-[var(--muted)]"}`}>{f.badge}</span>
            </button>
          ))}
          <div className="flex-1" />
          <button
            data-testid="studio-my-designs-btn"
            onClick={() => setShowDesigns(true)}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-[10px] text-sm font-semibold border border-[var(--border-c)] hover:border-[var(--glow)]"
            title="My saved designs"
          >
            <FolderOpen size={15} weight="duotone" /> My designs
            {savedDesigns.length > 0 && (
              <span className="text-[10px] font-mono bg-[var(--glow)]/20 text-[var(--glow)] px-1.5 rounded">{savedDesigns.length}</span>
            )}
          </button>
          <button
            data-testid="studio-save-btn"
            onClick={saveDesign}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-[10px] text-sm font-semibold surface hover:border-[var(--glow)]"
          >
            <FloppyDisk size={15} weight="bold" /> Save
          </button>
          <button
            data-testid="studio-export-btn"
            onClick={exportPng}
            disabled={exporting}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-bold bg-[var(--coral)] text-white hover:opacity-90 disabled:opacity-60"
          >
            <Download size={15} weight="bold" /> {exporting ? "…" : "Export PNG"}
          </button>
        </div>
      </div>

      {/* My Designs modal */}
      {showDesigns && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setShowDesigns(false)}
          data-testid="my-designs-modal"
        >
          <div
            className="surface rounded-[16px] p-5 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><FolderOpen size={20} weight="duotone" /> My designs</h3>
              <button data-testid="close-designs" onClick={() => setShowDesigns(false)} className="p-2 text-[var(--muted)] hover:text-[var(--text)]">
                <X size={18} weight="bold" />
              </button>
            </div>
            <button
              data-testid="new-blank-btn"
              onClick={() => { newBlank(); setShowDesigns(false); }}
              className="w-full mb-3 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-[10px] surface hover:border-[var(--glow)] text-sm font-semibold"
            >
              <Plus size={14} weight="bold" /> Start blank design
            </button>
            {savedDesigns.length === 0 ? (
              <p className="text-sm text-[var(--muted)] text-center py-8">No saved designs yet. Hit "Save" to keep your work here.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {savedDesigns.map((d) => {
                  const fmt = FORMATS.find((f) => f.key === d.formatKey) || FORMATS[0];
                  return (
                    <div
                      key={d.id}
                      data-testid={`saved-design-${d.id}`}
                      className="surface rounded-[10px] p-3 flex items-center gap-3 hover:border-[var(--glow)] transition-colors"
                    >
                      <button onClick={() => loadDesign(d)} className="flex-1 text-left min-w-0">
                        <p className="text-sm font-semibold truncate">{d.name}</p>
                        <p className="text-[11px] text-[var(--muted)]">{fmt.label} · {d.elements.length} element{d.elements.length === 1 ? "" : "s"} · {new Date(d.updatedAt).toLocaleDateString()}</p>
                      </button>
                      <button
                        data-testid={`delete-design-${d.id}`}
                        onClick={() => deleteDesign(d.id)}
                        className="p-2 text-[var(--muted)] hover:text-[var(--coral)]"
                        title="Delete"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-[var(--muted)] mt-4 text-center">
              Designs are saved to this browser. Sign in coming soon to sync across devices.
            </p>
          </div>
        </div>
      )}

      {/* Sticker picker modal */}
      {showStickers && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setShowStickers(false)}
          data-testid="sticker-picker-modal"
        >
          <div
            className="surface rounded-[16px] p-5 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Sticker size={20} weight="duotone" /> Stickers</h3>
              <button data-testid="close-stickers" onClick={() => setShowStickers(false)} className="p-2 text-[var(--muted)] hover:text-[var(--text)]">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
              {STICKER_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`sticker-cat-${c.key}`}
                  onClick={() => setStickerCat(c.key)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    stickerCat === c.key ? "bg-[var(--glow)] text-white" : "border border-[var(--border-c)] text-[var(--muted)] hover:text-[var(--text)]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {STICKERS[stickerCat].map((s) => (
                <button
                  key={s.key}
                  data-testid={`sticker-${s.key}`}
                  onClick={() => addSticker(s.key)}
                  title={s.label}
                  className="aspect-square surface rounded-[10px] p-3 hover:border-[var(--glow)] transition-colors flex items-center justify-center"
                >
                  <div
                    style={{ width: "100%", height: "100%", color: "var(--text)" }}
                    dangerouslySetInnerHTML={{ __html: s.svg.replace("<svg ", '<svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" ') }}
                  />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[var(--muted)] mt-4 text-center">Click any sticker to add it to your canvas. Recolour it from the right panel.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[190px_1fr_280px] gap-4">
        {/* LEFT — Elements */}
        <aside className="surface rounded-[14px] p-4 space-y-2 self-start order-2 lg:order-1">
          <p className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold mb-2">Elements</p>
          <button
            data-testid="add-text-btn"
            onClick={addText}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-[10px] surface hover:border-[var(--glow)] text-sm font-semibold transition-colors"
          >
            <TextAa size={16} weight="bold" /> Add text
          </button>
          <button
            data-testid="upload-image-btn"
            onClick={() => imgInputRef.current?.click()}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-[10px] surface hover:border-[var(--glow)] text-sm font-semibold transition-colors"
          >
            <ImageIcon size={16} weight="bold" /> Upload image
          </button>
          <button
            data-testid="add-sticker-btn"
            onClick={() => setShowStickers(true)}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-[10px] surface hover:border-[var(--glow)] text-sm font-semibold transition-colors"
          >
            <Sticker size={16} weight="bold" /> Add sticker
          </button>
          <button
            data-testid="magic-layers-btn"
            onClick={() => magicInputRef.current?.click()}
            disabled={magicBusy}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-[10px] surface hover:border-[var(--glow)] text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <MagicWand size={16} weight="bold" className={magicBusy ? "animate-pulse" : ""} />
            {magicBusy ? "Cutting out…" : "Magic Layers"}
          </button>
          <p className="text-[10px] text-[var(--muted)] leading-snug">
            Upload a photo — AI cuts out the subject so text sits behind it. Runs in your browser.
          </p>
          <input
            ref={imgInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="element-image-input"
            onChange={(e) => e.target.files?.[0] && addImage(e.target.files[0])}
          />
          <input
            ref={magicInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="magic-image-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) addMagic(f); e.target.value = ""; }}
          />

          <div className="border-t border-[var(--border-c)] my-3" />
          <p className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold mb-2">Shapes</p>
          <div className="flex gap-1 mb-2">
            {SHAPE_CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`shape-cat-${c.key}`}
                onClick={() => setShapeCat(c.key)}
                className={`flex-1 px-1.5 py-1 rounded-full text-[10px] font-semibold transition-colors ${
                  shapeCat === c.key ? "bg-[var(--glow)] text-white" : "border border-[var(--border-c)] text-[var(--muted)] hover:text-[var(--text)]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {SHAPES[shapeCat].map((s) => (
              <button
                key={s.key}
                data-testid={`shape-${s.key}`}
                onClick={() => addShape(s.key)}
                title={s.label}
                className="aspect-square surface rounded-[8px] p-1.5 hover:border-[var(--glow)] transition-colors flex items-center justify-center"
              >
                <div
                  style={{ width: "100%", height: "100%", color: "var(--text)" }}
                  dangerouslySetInnerHTML={{ __html: shapePreviewSvg(s) }}
                />
              </button>
            ))}
          </div>

          <div className="border-t border-[var(--border-c)] my-3" />
          <p className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold mb-2">Background</p>
          <div className="flex items-center gap-2">
            <label className="flex-1 flex items-center gap-2 px-3 py-2 rounded-[10px] surface">
              <span className="text-xs text-[var(--muted)]">Color</span>
              <input
                data-testid="bg-color"
                type="color"
                value={bgColor}
                onChange={(e) => { setBgColor(e.target.value); setBgImage(null); }}
                className="ml-auto w-6 h-6 rounded border-0 bg-transparent cursor-pointer"
              />
            </label>
          </div>
          <button
            data-testid="bg-upload-btn"
            onClick={() => bgInputRef.current?.click()}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-[10px] surface hover:border-[var(--glow)] text-xs font-semibold transition-colors"
          >
            <ImageIcon size={14} /> Upload background
          </button>
          <input
            ref={bgInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadBg(e.target.files[0])}
          />
          {bgImage && (
            <button onClick={() => setBgImage(null)} className="w-full text-[11px] text-[var(--muted)] hover:text-[var(--coral)] mt-1">
              Remove background image
            </button>
          )}

          <div className="border-t border-[var(--border-c)] my-3" />
          <p className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold mb-2">Layers</p>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {elements.length === 0 && <p className="text-xs text-[var(--muted)]">No elements yet.</p>}
            {[...elements].reverse().map((e, i) => (
              <button
                key={e.id}
                data-testid={`layer-${i}`}
                onClick={() => setSelectedId(e.id)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs truncate ${
                  selectedId === e.id ? "bg-[var(--glow)]/15 border border-[var(--glow)]/40" : "border border-transparent hover:bg-[var(--bg)]"
                }`}
              >
                {e.type === "text" ? `T · ${e.text.split("\n")[0]}` : e.type === "svg" ? `◆ ${getSticker(e.stickerKey)?.label || "Sticker"}` : e.type === "shape" ? `⬟ ${getShape(e.shapeKey)?.label || "Shape"}` : e.type === "magic" ? "✦ Magic photo" : e.isCutout ? "✂ Cut-out" : "Image"}
              </button>
            ))}
          </div>
        </aside>

        {/* CENTER — Canvas */}
        <div className="flex flex-col items-center order-1 lg:order-2">
          <div
            ref={stageWrapRef}
            className="relative rounded-[14px] bg-black/40"
            style={{ width: format.w * scale, height: format.h * scale, maxWidth: "100%", overflow: "visible" }}
            onMouseDown={(e) => { if (e.target === e.currentTarget || e.target === stageRef.current) setSelectedId(null); }}
          >
            <div
              ref={stageRef}
              data-testid="studio-stage"
              style={{
                width: format.w,
                height: format.h,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "relative",
                touchAction: "none",
                ...canvasBg,
              }}
            >
              {elements.map((el) => (
                <ElementView
                  key={el.id}
                  el={el}
                  scale={scale}
                  format={format}
                  selected={selectedId === el.id}
                  onSelect={setSelectedId}
                  onUpdate={(patch) => updateEl(el.id, patch)}
                  onDragging={handleDragging}
                  onDragEnd={handleDragEnd}
                  onResizing={handleResizing}
                />
              ))}
              {/* Magic subject overlays — always above normal elements, below "over subject" ones */}
              {elements.filter((e) => e.type === "magic" && e.subjectSrc).map((el) => {
                const live = liveEl && liveEl.id === el.id ? liveEl : null;
                return (
                <div
                  key={`subj-${el.id}`}
                  data-testid={`subject-overlay-${el.id}`}
                  style={{
                    position: "absolute",
                    left: live?.x ?? el.x,
                    top: live?.y ?? el.y,
                    width: live?.width ?? el.width,
                    height: live?.height ?? el.height,
                    transform: `rotate(${el.rotation}deg)`,
                    transformOrigin: "center center",
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                >
                  <SubjectOverlay el={el} />
                </div>
                );
              })}
              {/* Snap guide lines */}
              {snapLines.map((l, i) => (
                <div
                  key={i}
                  data-testid={`snap-${l.o}-${Math.round(l.pos)}`}
                  style={{
                    position: "absolute",
                    background: l.kind === "center" ? "#FF4D4D" : "#7C5CFF",
                    pointerEvents: "none",
                    zIndex: 25,
                    ...(l.o === "v"
                      ? { left: l.pos, top: 0, bottom: 0, width: 2 / scale }
                      : { top: l.pos, left: 0, right: 0, height: 2 / scale }),
                  }}
                />
              ))}
            </div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-3 text-center max-w-md">
            Drag elements to move · pull corners to resize · purple handle rotates. Snap lines appear when you align to canvas centre or edges.
          </p>
        </div>

        {/* RIGHT — Inspector */}
        <aside className="surface rounded-[14px] p-4 self-start space-y-4 order-3">
          {!selected && <p className="text-xs text-[var(--muted)]">Select an element to edit its style.</p>}
          {selected && (
            <>
              <div className="flex items-center gap-1">
                <button data-testid="duplicate-btn" onClick={duplicateSelected} title="Duplicate" className="flex-1 text-xs px-2 py-2 rounded surface hover:border-[var(--glow)] inline-flex items-center justify-center gap-1"><Copy size={13} /> Duplicate</button>
                <button data-testid="delete-btn" onClick={deleteSelected} title="Delete" className="flex-1 text-xs px-2 py-2 rounded border border-[var(--border-c)] text-[var(--coral)] hover:border-[var(--coral)] inline-flex items-center justify-center gap-1"><Trash size={13} /> Delete</button>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid="front-btn" onClick={bringToFront} title="Bring to front" className="flex-1 text-xs px-2 py-2 rounded surface hover:border-[var(--glow)] inline-flex items-center justify-center gap-1"><StackSimple size={13} /> Front</button>
                <button data-testid="back-btn" onClick={sendToBack} title="Send to back" className="flex-1 text-xs px-2 py-2 rounded surface hover:border-[var(--glow)] inline-flex items-center justify-center gap-1"><ArrowDown size={13} /> Back</button>
              </div>

              {selected.type !== "magic" && elements.some((e) => e.type === "magic") && (
                <label className="flex items-center justify-between gap-2 text-xs font-semibold px-1 cursor-pointer">
                  <span>Above Magic subject</span>
                  <input
                    type="checkbox"
                    data-testid="over-subject-toggle"
                    checked={!!selected.overSubject}
                    onChange={(e) => updateSelected({ overSubject: e.target.checked })}
                    className="accent-[var(--glow)] w-4 h-4"
                  />
                </label>
              )}

              <div>
                <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Rotation</span>
                <input
                  data-testid="rotate-slider"
                  type="range"
                  min="-180"
                  max="180"
                  value={selected.rotation}
                  onChange={(e) => updateSelected({ rotation: parseInt(e.target.value, 10) })}
                  className="w-full mt-2"
                />
                <span className="text-xs text-[var(--muted)]">{selected.rotation}°</span>
              </div>

              {selected.type === "text" && (
                <>
                  <div>
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Text</span>
                    <textarea
                      data-testid="text-content"
                      value={selected.text}
                      onChange={(e) => updateSelected({ text: e.target.value })}
                      rows={2}
                      className="w-full mt-2 min-w-0 bg-[var(--bg)] border border-[var(--border-c)] rounded-[10px] px-3 py-2 text-sm outline-none focus:border-[var(--glow)]"
                    />
                  </div>
                  <div>
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Font</span>
                    <select
                      data-testid="font-select"
                      value={selected.fontFamily}
                      onChange={(e) => updateSelected({ fontFamily: e.target.value })}
                      className="w-full mt-2 bg-[var(--bg)] border border-[var(--border-c)] rounded-[10px] px-3 py-2 text-sm outline-none focus:border-[var(--glow)]"
                    >
                      {FONTS.map((f) => <option key={f.family} value={f.family}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Size</span>
                    <input
                      data-testid="font-size"
                      type="range"
                      min={Math.round(format.w * 0.02)}
                      max={Math.round(format.w * 0.25)}
                      value={selected.fontSize}
                      onChange={(e) => updateSelected({ fontSize: parseInt(e.target.value, 10) })}
                      className="w-full mt-2"
                    />
                    <span className="text-xs text-[var(--muted)]">{selected.fontSize}px</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Fill</span>
                      <input type="color" data-testid="fill-color" value={selected.color || "#FFFFFF"} onChange={(e) => updateSelected({ color: e.target.value, gradient: null })} className="w-full h-9 mt-2 rounded border-0 bg-transparent cursor-pointer" />
                    </label>
                    <label>
                      <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Stroke</span>
                      <input type="color" data-testid="stroke-color" value={selected.strokeColor || "#000000"} onChange={(e) => updateSelected({ strokeColor: e.target.value })} className="w-full h-9 mt-2 rounded border-0 bg-transparent cursor-pointer" />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Stroke W</span>
                      <input type="range" data-testid="stroke-width" min="0" max="40" value={selected.strokeWidth || 0} onChange={(e) => updateSelected({ strokeWidth: parseInt(e.target.value, 10) })} className="w-full mt-2" />
                    </label>
                    <label>
                      <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Shadow</span>
                      <input type="range" data-testid="shadow-input" min="0" max="40" value={selected.shadow || 0} onChange={(e) => updateSelected({ shadow: parseInt(e.target.value, 10) })} className="w-full mt-2" />
                    </label>
                  </div>

                  <div>
                    <p className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold mb-2">Style presets</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {PRESETS.map((p) => (
                        <button
                          key={p.key}
                          data-testid={`preset-${p.key}`}
                          onClick={() => applyPreset(p)}
                          className="text-xs font-semibold px-2 py-1.5 rounded-[8px] border border-[var(--border-c)] hover:border-[var(--glow)] hover:text-[var(--glow)] transition-colors"
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {selected.type === "image" && (
                <p className="text-xs text-[var(--muted)]">Image · drag corners to resize, drag the purple dot to rotate.</p>
              )}

              {selected.type === "magic" && (
                <>
                  <button
                    data-testid="detach-subject-btn"
                    onClick={detachSubject}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[10px] surface hover:border-[var(--glow)] text-xs font-semibold transition-colors"
                  >
                    <Copy size={13} /> Detach subject as own layer
                  </button>
                  <p className="text-[10px] text-[var(--muted)] leading-snug">
                    Creates a free copy of the cut-out person you can resize, move and rotate independently.
                  </p>
                  <label className="block">
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Edge feather</span>
                    <input
                      type="range"
                      data-testid="feather-slider"
                      min="0"
                      max="30"
                      value={selected.feather || 0}
                      onChange={(e) => updateSelected({ feather: parseInt(e.target.value, 10) })}
                      className="w-full mt-2"
                    />
                    <span className="text-xs text-[var(--muted)]">{selected.feather || 0}px · softens the cut-out edge</span>
                  </label>
                  <p className="text-xs text-[var(--muted)]">
                    ✦ Magic layer — the cut-out subject always renders in front, so text and shapes sit <em>between</em> the photo and the subject. Tick "Above Magic subject" on any element to bring it fully in front.
                  </p>
                </>
              )}

              {selected.type === "shape" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Fill</span>
                      <input
                        type="color"
                        data-testid="shape-fill"
                        value={selected.fill === "none" ? "#000000" : selected.fill}
                        onChange={(e) => updateSelected({ fill: e.target.value })}
                        className="w-full h-9 mt-2 rounded border-0 bg-transparent cursor-pointer"
                      />
                    </label>
                    <label>
                      <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Stroke</span>
                      <input
                        type="color"
                        data-testid="shape-stroke"
                        value={selected.stroke === "none" ? "#FFFFFF" : selected.stroke}
                        onChange={(e) => updateSelected({ stroke: e.target.value, strokeW: selected.strokeW > 0 ? selected.strokeW : 4 })}
                        className="w-full h-9 mt-2 rounded border-0 bg-transparent cursor-pointer"
                      />
                    </label>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="shape-no-fill"
                      checked={selected.fill === "none"}
                      onChange={(e) => updateSelected({ fill: e.target.checked ? "none" : "#7C5CFF" })}
                      className="accent-[var(--glow)] w-4 h-4"
                    />
                    Transparent fill (outline only)
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Stroke width</span>
                    <input
                      type="range"
                      data-testid="shape-stroke-width"
                      min="0"
                      max="24"
                      value={selected.strokeW || 0}
                      onChange={(e) => updateSelected({ strokeW: parseInt(e.target.value, 10), stroke: selected.stroke === "none" ? "#FFFFFF" : selected.stroke })}
                      className="w-full mt-2"
                    />
                    <span className="text-xs text-[var(--muted)]">{selected.strokeW || 0}px</span>
                  </label>
                </>
              )}

              {selected.type === "svg" && (
                <>
                  <label className="block">
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-widest font-semibold">Sticker color</span>
                    <input
                      data-testid="sticker-color"
                      type="color"
                      value={selected.color || "#FFFFFF"}
                      onChange={(e) => updateSelected({ color: e.target.value })}
                      className="w-full h-9 mt-2 rounded border-0 bg-transparent cursor-pointer"
                    />
                  </label>
                  <p className="text-[11px] text-[var(--muted)]">Sticker · drag corners to resize, drag the purple dot to rotate.</p>
                </>
              )}
            </>
          )}
        </aside>
      </div>

      <style>{`
        .el-selected { outline: 2px solid #7C5CFF; outline-offset: 0; }
        .el-idle:hover { outline: 1px dashed rgba(124,92,255,0.6); }
      `}</style>
    </ToolShell>
  );
}
